/**
 * @fileoverview Empty - Empty state component
 *
 * Purpose: Display empty states with icon, title, description, and actions
 * Dependencies: class-variance-authority
 * Used by: Empty search results, no data states, zero states
 *
 * Components exported:
 * - Empty: Root empty state container
 * - EmptyHeader: Header section
 * - EmptyMedia: Icon or image placeholder
 * - EmptyTitle: Title text
 * - EmptyDescription: Description text
 * - EmptyContent: Content wrapper
 *
 * Features:
 * - Centered layout
 * - Icon or image variants
 * - Dashed border styling
 * - Flexible content arrangement
 * - Call-to-action support
 *
 * @example
 * <Empty>
 *   <EmptyHeader>
 *     <EmptyMedia variant="icon">
 *       <SearchIcon className="h-6 w-6" />
 *     </EmptyMedia>
 *     <EmptyTitle>No results found</EmptyTitle>
 *     <EmptyDescription>
 *       Try adjusting your search to find what you're looking for.
 *     </EmptyDescription>
 *   </EmptyHeader>
 *   <Button>Clear Search</Button>
 * </Empty>
 */

import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/misc";

function Empty({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty"
      className={cn(
        "flex min-w-0 flex-1 flex-col items-center justify-center gap-6 text-balance rounded-lg border-dashed p-6 text-center md:p-12",
        className,
      )}
      {...props}
    />
  );
}

function EmptyHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-header"
      className={cn(
        "flex max-w-sm flex-col items-center gap-2 text-center",
        className,
      )}
      {...props}
    />
  );
}

const emptyMediaVariants = cva(
  "mb-2 flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        icon: "flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground [&_svg:not([class*='size-'])]:size-6",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function EmptyMedia({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof emptyMediaVariants>) {
  return (
    <div
      data-slot="empty-icon"
      data-variant={variant}
      className={cn(emptyMediaVariants({ variant, className }))}
      {...props}
    />
  );
}

function EmptyTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-title"
      className={cn("text-lg font-medium tracking-tight", className)}
      {...props}
    />
  );
}

function EmptyDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <div
      data-slot="empty-description"
      className={cn(
        "text-sm/relaxed text-muted-foreground [&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4",
        className,
      )}
      {...props}
    />
  );
}

function EmptyContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-content"
      className={cn(
        "flex w-full min-w-0 max-w-sm flex-col items-center gap-4 text-balance text-sm",
        className,
      )}
      {...props}
    />
  );
}

export {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
  EmptyMedia,
};
